/**
 * Agents v3 — "Build your own agent", the WHOLE flow in one popup over /agents,
 * for creating AND editing a custom agent:
 *
 *   wizard   → the fixed questions (BuilderWizard), or a free description.
 *              Editing opens the wizard pre-filled from the answers stored
 *              with the agent.
 *   checking → the answers go to the builder (the agent-chat sidecar's
 *              relay) as ONE "Intake brief" turn; it checks them like an
 *              expert. Shown as a small progress rail, not a spinner.
 *   question → if something is missing or vague the builder asks here, in the
 *              same popup; the answer rides on the same transcript
 *   confirm  → the proposal as a plain summary (name, what it does, schedule,
 *              what it reads, KPI use, estimated cost). The agent's working
 *              prompt is NOT shown — it is the builder's job.
 *   creating → POST the blueprint (+ the wizard answers, so editing can reopen
 *              them) and activate it for the brand on the exact schedule —
 *              or PATCH the definition when editing
 *   done     → "…is ready" / "…is updated" with "Show me under Agents"
 *
 * The popup owns the builder session (token + brand context) and the
 * transcript; the sidecar is stateless. Nothing here navigates.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Popup from '../../../ui/popup/Popup';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import {
  activateAgent,
  createCustomAgent,
  startBuilderSession,
  updateCustomAgent,
  type AgentCadence,
  type BuilderSession,
  type CustomAgentBlueprint,
} from '../../../lib/api/client-reports/client-agents-api';
import { streamBuilderTurn, type BuilderTranscriptMessage } from '../../../lib/chat/agent-builder-stream';
import { BUILDER_IDLE_TIMEOUT_MS, createIdleTimeout, type IdleTimeout } from '../../../lib/chat/idle-timeout';
import { composeCronSchedule, describeSchedule } from '../../client-reports/agents/agent-helpers';
import BuilderWizard from './BuilderWizard';
import { composeIntakeBrief, intakeSteps, platformLabel, type IntakeAnswers } from './builder-intake';

const DEFAULT_TIMEZONE = 'Europe/Amsterdam';

const lowerFirst = (text: string): string => `${text.charAt(0).toLowerCase()}${text.slice(1)}`;

/** The confirm card's "Delivers" line: [translation key, English]. */
const OUTPUT_LABEL: Record<string, [string, string]> = {
  findings: ['AgentBuilderOutputFindings', 'A findings brief for your team (bullets + a one-page HTML)'],
  client_report: ['AgentBuilderOutputClientReport', 'A client-facing report (goes through your review queue)'],
  workbook: ['AgentBuilderOutputWorkbook', 'An Excel workbook'],
};

/** Runs per month × the per-run cap — an upper bound, like the server's. */
export function estimateMonthlyCents(blueprint: CustomAgentBlueprint): number {
  const runs = blueprint.schedule.cadence === 'daily' ? 30 : blueprint.schedule.cadence === 'monthly' ? 1 : 4;
  const budget =
    blueprint.budget_cents ??
    (blueprint.output_mode === 'client_report' ? 400 : blueprint.output_mode === 'workbook' ? 300 : 150);
  return runs * budget;
}

/**
 * Editing an agent built before answers were stored: the best wizard
 * pre-fill the blueprint allows (the trigger cannot be recovered — it is
 * asked again).
 */
export function answersFromBlueprint(bp: CustomAgentBlueprint, timezone: string): IntakeAnswers {
  const stored = bp.intake && typeof bp.intake === 'object' ? (bp.intake as IntakeAnswers) : null;
  if (stored && Object.keys(stored).length > 0) return stored;
  const s = bp.schedule;
  return {
    goal: [bp.description, bp.tagline].filter(Boolean).join(' ') || bp.name,
    sources: bp.platforms?.length ? bp.platforms : undefined,
    kpis: bp.focus_metrics?.length ? bp.focus_metrics : undefined,
    frequency: s
      ? { cron: composeCronSchedule({ cadence: s.cadence, day: s.day, hour: s.hour, minute: s.minute }), timezone: s.timezone || timezone }
      : undefined,
    output: bp.output_mode,
  };
}

type Phase = 'wizard' | 'describe' | 'checking' | 'question' | 'confirm' | 'creating' | 'done' | 'error';

/** The checking rail: what the builder is doing, in the user's terms. */
const CHECK_STEPS: Array<[string, string]> = [
  ['AgentBuilderCheckReading', 'Reading your answers'],
  ['AgentBuilderCheckGaps', 'Checking for gaps'],
  ['AgentBuilderCheckWriting', 'Writing the agent'],
];

interface Props {
  open: boolean;
  shopSlug: string;
  brandName: string;
  /** Edit an existing custom agent (its definition id) instead of creating one. */
  editDefinitionId?: string | null;
  onClose: () => void;
  /** The agent exists (created or updated; switched on unless `activated` is false). */
  onCreated: (result: { agentType: string; name: string; activated: boolean; show: boolean; edited: boolean }) => void;
}

const BuilderWizardPopup = ({ open, shopSlug, brandName, editDefinitionId = null, onClose, onCreated }: Props) => {
  const [session, setSession] = useState<BuilderSession | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('wizard');
  const [answers, setAnswers] = useState<IntakeAnswers>({});
  const [transcript, setTranscript] = useState<BuilderTranscriptMessage[]>([]);
  /** The builder's latest words (a question, or the line before a proposal). */
  const [reply, setReply] = useState('');
  const [streaming, setStreaming] = useState('');
  /** How far the checking rail is: 0 reading, 1 checking, 2 writing, 3 done. */
  const [checkStage, setCheckStage] = useState(0);
  const [blueprint, setBlueprint] = useState<CustomAgentBlueprint | null>(null);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ agentType: string; name: string; activated: boolean } | null>(null);
  /** Asking for a change on the confirm step. */
  const [changing, setChanging] = useState(false);

  const { t } = useTranslation();
  const abortRef = useRef<AbortController | null>(null);
  /** The idle timer of the builder turn or create in flight. */
  const idleRef = useRef<IdleTimeout | null>(null);
  /** What "Try again" repeats after an error: the last builder turn or the create. */
  const retryTargetRef = useRef<'turn' | 'create'>('turn');
  /**
   * Idempotency id for creating this blueprint. Try again after a create that
   * timed out sends the same id, so central-api returns the agent the first
   * request may already have made. A new session or builder turn clears it.
   */
  const clientRequestIdRef = useRef<string | null>(null);
  const editing = !!editDefinitionId;

  // Every opening is a fresh flow (derived state during render — the wizard
  // must mount on empty answers, not flash the previous run's).
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setPhase('wizard');
      setAnswers({});
      setTranscript([]);
      setReply('');
      setStreaming('');
      setCheckStage(0);
      setBlueprint(null);
      setInput('');
      setError(null);
      setCreated(null);
      setChanging(false);
      retryTargetRef.current = 'turn';
      clientRequestIdRef.current = null;
    }
  }

  // The builder session: the brand context for the chips + the token to talk.
  // Editing: the session carries the saved blueprint → the wizard pre-fills.
  // The latest t, so a language switch does not restart the session.
  const tRef = useRef(t);
  tRef.current = t;
  useEffect(() => {
    const t = tRef.current;
    if (!open || !shopSlug) return;
    let cancelled = false;
    setSessionError(null);
    setSession(null);
    startBuilderSession(shopSlug, editDefinitionId ?? undefined)
      .then((s) => {
        if (cancelled) return;
        // Answers BEFORE the session: the wizard mounts when the session lands
        // and seeds its first draft from whatever answers exist at that moment.
        if (editDefinitionId && s.blueprint) {
          setAnswers(answersFromBlueprint(s.blueprint, (s.context?.timezone as string | undefined) || DEFAULT_TIMEZONE));
        }
        setSession(s);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setSessionError(
          err?.status === 503
            ? t('AgentBuilderNotEnabled', 'The agent builder is not switched on for this environment yet.')
            : err?.status === 404
            ? t('AgentBuilderAgentGone', 'That agent no longer exists.')
            : err?.message || t('AgentBuilderStartFailed', 'Failed to start the builder.')
        );
      });
    return () => {
      cancelled = true;
      abortRef.current?.abort();
      idleRef.current?.clear();
    };
  }, [open, shopSlug, editDefinitionId]);

  // The checking rail moves with time when the builder is quiet, and jumps
  // ahead the moment something arrives.
  useEffect(() => {
    if (phase !== 'checking') return;
    const t = setTimeout(() => setCheckStage((s) => Math.max(s, 1)), 1500);
    return () => clearTimeout(t);
  }, [phase]);

  const timezone = (session?.context?.timezone as string | undefined) || DEFAULT_TIMEZONE;

  /** One turn to the builder: the transcript + this user text. */
  const send = useCallback(
    async (text: string, prior: BuilderTranscriptMessage[]) => {
      if (!session || !text.trim()) return;
      const next: BuilderTranscriptMessage[] = [...prior, { role: 'user', text, blueprint: null, tool_use_id: null }];
      setTranscript(next);
      setPhase('checking');
      setCheckStage(0);
      setError(null);
      setStreaming('');
      setChanging(false);

      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      retryTargetRef.current = 'turn';
      // A new turn can change the blueprint: its create is a new request.
      clientRequestIdRef.current = null;

      // A builder that goes quiet ends the turn: abort the stream and offer
      // Try again, so the popup can be closed again.
      idleRef.current?.clear();
      const idle = createIdleTimeout(BUILDER_IDLE_TIMEOUT_MS, () => {
        ac.abort();
        setStreaming('');
        setError(t('AgentBuilderTimedOut', 'The builder did not respond in time. Please try again.'));
        setPhase('error');
      });
      idleRef.current = idle;

      let live = session;
      let liveText = '';
      let proposed: { toolUseId: string; blueprint: CustomAgentBlueprint } | null = null;
      let failure: string | null = null;
      try {
        // The token is short-lived; re-mint when it is about to expire.
        if (live.expires_at * 1000 - Date.now() < 60_000) {
          live = await startBuilderSession(shopSlug, editDefinitionId ?? undefined);
          setSession(live);
        }
        await streamBuilderTurn({
          chatUrl: live.chat_url,
          token: live.token,
          messages: next,
          signal: ac.signal,
          onEvent: (ev) => {
            if (ac.signal.aborted) return;
            idle.reset();
            if (ev.type === 'delta') {
              liveText += ev.text;
              setStreaming(liveText);
              setCheckStage((s) => Math.max(s, 1));
            } else if (ev.type === 'blueprint') {
              proposed = { toolUseId: ev.toolUseId, blueprint: ev.blueprint };
              setCheckStage(3);
            } else if (ev.type === 'done') {
              if (ev.text) liveText = ev.text;
            } else if (ev.type === 'error') {
              failure = ev.message;
            }
          },
        });
      } catch (err: any) {
        failure = err?.name === 'AbortError' ? null : err?.message || t('AgentBuilderStoppedResponding', 'The builder stopped responding.');
      }
      idle.clear();
      if (ac.signal.aborted) return;

      const p = proposed as { toolUseId: string; blueprint: CustomAgentBlueprint } | null;
      setTranscript([
        ...next,
        { role: 'assistant', text: liveText, blueprint: p?.blueprint ?? null, tool_use_id: p?.toolUseId ?? null },
      ]);
      setStreaming('');
      setReply(liveText);
      if (p) {
        setBlueprint(p.blueprint);
        setPhase('confirm');
      } else if (failure && !liveText) {
        setError(failure);
        setPhase('error');
      } else {
        setPhase('question');
      }
    },
    [session, shopSlug, editDefinitionId, t]
  );

  const finishWizard = (final: IntakeAnswers) => {
    setAnswers(final);
    void send(composeIntakeBrief(final, brandName), []);
  };

  const answer = () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    void send(text, transcript);
  };

  /** "Ask for a change" on the confirm step: the request rides on the transcript. */
  const requestChange = () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    void send(text, transcript);
  };

  const retry = () => {
    if (retryTargetRef.current === 'create') {
      void create();
      return;
    }
    // Re-send the last user turn.
    const idx = [...transcript].reverse().findIndex((m) => m.role === 'user');
    if (idx < 0) return setPhase('wizard');
    const at = transcript.length - 1 - idx;
    void send(transcript[at].text, transcript.slice(0, at));
  };

  const create = async () => {
    if (!blueprint || !shopSlug) return;
    setPhase('creating');
    setError(null);
    retryTargetRef.current = 'create';

    // The save request gets the same idle limit as a builder turn. Once it
    // fires the request is aborted and a late answer is ignored: nothing is
    // switched on and the popup does not report an agent.
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    idleRef.current?.clear();
    const idle = createIdleTimeout(BUILDER_IDLE_TIMEOUT_MS, () => {
      ac.abort();
      setError(
        editDefinitionId
          ? t('AgentBuilderSaveTimedOut', 'Saving the agent took too long. Please try again.')
          : t('AgentBuilderCreateTimedOut', 'Creating the agent took too long. Please try again.')
      );
      setPhase('error');
    });
    idleRef.current = idle;

    // The wizard answers travel with the blueprint so editing reopens them.
    const payload: CustomAgentBlueprint = { ...blueprint, intake: answers };
    try {
      if (editDefinitionId) {
        const result = await updateCustomAgent(editDefinitionId, payload, { signal: ac.signal });
        if (idle.fired) return;
        idle.clear();
        setCreated({ agentType: result.data.agent_type, name: result.data.name, activated: result.deployments_failed === 0 });
        setPhase('done');
        return;
      }
      if (!clientRequestIdRef.current) clientRequestIdRef.current = uuidv4();
      const definition = await createCustomAgent(payload, {
        signal: ac.signal,
        clientRequestId: clientRequestIdRef.current,
      });
      if (idle.fired) return;
      // The agent exists now; switching it on reports its own failure.
      idle.clear();
      let activated = true;
      try {
        const s = blueprint.schedule;
        await activateAgent(shopSlug, {
          agent_type: definition.agent_type,
          schedule: {
            cron: composeCronSchedule({ cadence: s.cadence, day: s.day, hour: s.hour, minute: s.minute }),
            timezone: s.timezone || timezone,
          },
        });
      } catch {
        activated = false;
      }
      setCreated({ agentType: definition.agent_type, name: definition.name, activated });
      setPhase('done');
    } catch (err: any) {
      if (idle.fired) return;
      idle.clear();
      setError(
        err?.message ||
          (editDefinitionId
            ? t('AgentBuilderSaveFailed', 'Failed to save the agent.')
            : t('AgentBuilderCreateFailed', 'Failed to create the agent.'))
      );
      setPhase('confirm');
    }
  };

  const scheduleWords = blueprint
    ? describeSchedule(
        composeCronSchedule({
          cadence: blueprint.schedule.cadence,
          day: blueprint.schedule.day,
          hour: blueprint.schedule.hour,
          minute: blueprint.schedule.minute,
        }),
        blueprint.schedule.timezone || timezone,
        t
      )
    : '';

  const busy = phase === 'checking' || phase === 'creating';
  const headline =
    phase === 'done' && created
      ? editing
        ? t('AgentBuilderUpdatedTitle', '{{name}} is updated', { name: created.name })
        : t('AgentBuilderReadyTitle', '{{name}} is ready', { name: created.name })
      : editing
      ? t('AgentBuilderEditTitle', 'Edit agent')
      : t('AgentsBuildOwnTitle', 'Build your own agent');

  const describeTitle = editing
    ? t('AgentBuilderDescribeChange', 'Describe what should change')
    : t('AgentBuilderDescribeAgent', 'Describe the agent you want');

  /** The first line of what the builder is saying, as a quiet status. */
  const statusLine = streaming.split('\n').find((l) => l.trim() !== '')?.trim() ?? '';

  return (
    <Popup trigger={open} setTrigger={busy ? () => undefined : onClose} additionalClassName='agentsHub__wizard-popup' overlayVariant='blur'>
      <div className={`agentsHub__wizard-popup-body agentsHub__wizard-popup-body--${phase}`} data-testid='builder-wizard-popup' data-phase={phase}>
        <div className='agentsHub__wizard-popup-head'>
          <h3 className='client-agents__confirm-title'>
            {headline}
            {phase !== 'done' && <span className='agentsHub__wizard-popup-for'> {t('AgentBuilderForBrand', 'for {{brand}}', { brand: brandName })}</span>}
          </h3>
        </div>

        {sessionError ? (
          <div className='agentsHub__error'>
            {sessionError}
            <div style={{ marginTop: '1.4rem' }}>
              <MainSquareButton type='button' value={t('Close', 'Close')} buttonType='light' normal onClick={onClose} />
            </div>
          </div>
        ) : !session ? (
          <p className='client-agents__muted'>{t('AgentBuilderStarting', 'Starting the builder…')}</p>
        ) : phase === 'wizard' ? (
          <BuilderWizard
            steps={intakeSteps(session.context)}
            answers={answers}
            timezone={timezone}
            cadences={session.context?.cadences as AgentCadence[] | undefined}
            brandName={brandName}
            onChange={setAnswers}
            onFinish={finishWizard}
            onSkip={() => setPhase('describe')}
          />
        ) : phase === 'describe' ? (
          <div className='agentsHub__wizard-panel' data-testid='builder-describe'>
            <h3 className='agentsHub__wizard-question'>{describeTitle}</h3>
            <p className='client-agents__muted'>
              {t(
                'AgentBuilderDescribeHint',
                'What it should watch, which data and KPIs, when to flag, how often, and what it delivers. The builder asks for anything missing.'
              )}
            </p>
            <textarea
              className='client-agents__textarea client-agents__textarea--tall'
              aria-label={describeTitle}
              placeholder={t('AgentBuilderDescribePlaceholder', 'For example: every Monday at 07:00, list Meta campaigns whose CPA is above our target…')}
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            <div className='agentsHub__wizard-actions'>
              <button type='button' className='client-agents__intake-skip' onClick={() => setPhase('wizard')}>
                {t('AgentBuilderAnswerQuestionsInstead', 'Answer the questions instead')}
              </button>
              <div className='agentsHub__wizard-actions-right'>
                <MainSquareButton
                  type='button'
                  value={t('AgentBuilderSendToBuilder', 'Send to the builder')}
                  buttonType='success'
                  normal
                  disabled={input.trim() === ''}
                  onClick={() => {
                    const text = input.trim();
                    setInput('');
                    void send(text, []);
                  }}
                />
              </div>
            </div>
          </div>
        ) : phase === 'checking' || phase === 'creating' ? (
          <div className='agentsHub__progress' data-testid='builder-checking' aria-live='polite' aria-busy='true'>
            <div className='agentsHub__progress-bar' role='progressbar' aria-valuemin={0} aria-valuemax={100} aria-valuenow={phase === 'creating' ? 90 : Math.min(85, 15 + checkStage * 25)}>
              <span
                className='agentsHub__progress-fill'
                style={{ width: `${phase === 'creating' ? 90 : Math.min(85, 15 + checkStage * 25)}%` }}
              />
            </div>
            {phase === 'creating' ? (
              <p className='agentsHub__progress-title'>{editing
                  ? t('AgentBuilderSaving', 'Saving the agent…')
                  : t('AgentBuilderCreating', 'Creating the agent and switching it on…')}</p>
            ) : (
              <ol className='agentsHub__progress-steps'>
                {CHECK_STEPS.map(([labelKey, labelText], i) => {
                  const label = t(labelKey, labelText);
                  const state = checkStage > i ? 'done' : checkStage === i ? 'active' : 'todo';
                  return (
                    <li key={label} className={`agentsHub__progress-step agentsHub__progress-step--${state}`}>
                      <span className='agentsHub__progress-mark' aria-hidden='true'>
                        {state === 'done' ? '✓' : i + 1}
                      </span>
                      <span>{label}</span>
                    </li>
                  );
                })}
              </ol>
            )}
            {phase === 'checking' && statusLine && (
              <p className='agentsHub__progress-status' data-testid='builder-status-line'>
                {statusLine.length > 140 ? `${statusLine.slice(0, 140)}…` : statusLine}
              </p>
            )}
          </div>
        ) : phase === 'question' ? (
          <div className='agentsHub__wizard-panel' data-testid='builder-question'>
            <span className='client-agents__intake-step'>{t('AgentBuilderHasQuestion', 'The builder has a question')}</span>
            <div className='agentsHub__wizard-reply'>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{reply}</ReactMarkdown>
            </div>
            <textarea
              className='client-agents__textarea'
              aria-label={t('AgentBuilderYourAnswer', 'Your answer')}
              placeholder={t('AgentBuilderYourAnswerPlaceholder', 'Your answer…')}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  answer();
                }
              }}
            />
            <div className='agentsHub__wizard-actions'>
              <button type='button' className='client-agents__intake-skip' onClick={() => setPhase('wizard')}>
                {t('AgentBuilderChangeAnswers', 'Change my answers')}
              </button>
              <div className='agentsHub__wizard-actions-right'>
                <MainSquareButton type='button' value={t('AgentBuilderAnswer', 'Answer')} buttonType='success' normal disabled={input.trim() === ''} onClick={answer} />
              </div>
            </div>
          </div>
        ) : phase === 'confirm' && blueprint ? (
          <div className='agentsHub__wizard-panel' data-testid='builder-confirm'>
            <span className='client-agents__intake-step'>{editing
                ? t('AgentBuilderReadyToSave', 'Ready to save — please confirm')
                : t('AgentBuilderReadyToCreate', 'Ready to create — please confirm')}</span>
            <h3 className='agentsHub__wizard-question'>{blueprint.name}</h3>
            {blueprint.tagline && <p className='client-agents__card-hint'>{blueprint.tagline}</p>}
            {blueprint.description && <p className='client-agents__muted'>{blueprint.description}</p>}
            <dl className='agentsHub__review-facts'>
              <dt>{t('AgentBuilderRuns', 'Runs')}</dt>
              <dd>{scheduleWords}</dd>
              <dt>{t('AgentBuilderDelivers', 'Delivers')}</dt>
              <dd>{OUTPUT_LABEL[blueprint.output_mode] ? t(OUTPUT_LABEL[blueprint.output_mode][0], OUTPUT_LABEL[blueprint.output_mode][1]) : blueprint.output_mode}</dd>
              <dt>{t('AgentBuilderReads', 'Reads')}</dt>
              <dd>{blueprint.platforms.length > 0 ? blueprint.platforms.map(platformLabel).join(', ') : t('AgentBuilderEveryPlatform', 'Every connected platform')}</dd>
              <dt>{t('AgentBuilderUsesKpiTargets', 'Uses your KPI targets')}</dt>
              <dd>
                {blueprint.focus_metrics.length > 0 || /kpi|target/i.test(blueprint.instructions)
                  ? `${t('AgentBuilderYes', 'Yes')}${blueprint.focus_metrics.length ? ` (${blueprint.focus_metrics.join(', ')})` : ''}`
                  : t('AgentBuilderNo', 'No')}
              </dd>
              <dt>{t('AgentBuilderEstimatedCost', 'Estimated cost')}</dt>
              <dd>
                {t('AgentBuilderCostPerMonth', 'up to €{{amount}} per month', {
                  amount: (estimateMonthlyCents(blueprint) / 100).toFixed(2),
                })}
              </dd>
            </dl>
            <p className='client-agents__muted'>
              {editing
                ? t('AgentBuilderEditNotice', 'Every brand that runs this agent picks up the change on its next run.')
                : t(
                    'AgentBuilderCreateNotice',
                    'It is switched on for {{brand}} as soon as you confirm. You can switch it off, change or remove it from its card under Agents at any time.',
                    { brand: brandName }
                  )}
            </p>

            {changing && (
              <div className='agentsHub__wizard-change'>
                <textarea
                  className='client-agents__textarea'
                  aria-label={t('AgentBuilderWhatShouldChange', 'What should change?')}
                  placeholder={t(
                    'AgentBuilderChangePlaceholder',
                    'What should change? For example: run on Fridays instead, or only look at campaigns above €200 spend…'
                  )}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                />
              </div>
            )}
            {error && (
              <p className='client-agents__validation' role='alert'>
                {error}
              </p>
            )}
            <div className='agentsHub__wizard-actions'>
              <button type='button' className='client-agents__intake-skip' onClick={() => setPhase('wizard')}>
                {t('AgentBuilderChangeAnswers', 'Change my answers')}
              </button>
              <div className='agentsHub__wizard-actions-right'>
                {changing ? (
                  <>
                    <MainSquareButton type='button' value={t('Cancel', 'Cancel')} buttonType='light' normal onClick={() => { setChanging(false); setInput(''); }} />
                    <MainSquareButton type='button' value={t('AgentBuilderSendChange', 'Send change')} buttonType='success' normal disabled={input.trim() === ''} onClick={requestChange} />
                  </>
                ) : (
                  <>
                    <MainSquareButton type='button' value={t('AgentBuilderAskForChange', 'Ask for a change')} buttonType='light' normal onClick={() => setChanging(true)} />
                    <MainSquareButton type='button' value={editing ? t('SaveChanges', 'Save changes') : t('AgentBuilderCreateAgent', 'Create agent')} buttonType='success' normal onClick={() => void create()} />
                  </>
                )}
              </div>
            </div>
          </div>
        ) : phase === 'done' && created ? (
          <div className='agentsHub__done' data-testid='builder-done'>
            <div className='agentsHub__done-mark' aria-hidden='true'>
              ✓
            </div>
            <div className='agentsHub__done-body'>
              <p className='client-agents__confirm-body'>
                {editing
                  ? created.activated
                    ? t(
                        'AgentBuilderSavedEveryBrand',
                        'Saved. Every brand that runs it{{when}} uses the new version from its next run.',
                        { when: scheduleWords ? ` — ${lowerFirst(scheduleWords)} —` : '' }
                      )
                    : t('AgentBuilderSavedPartial', 'Saved — but not every brand could be updated yet; AdUp has been notified.')
                  : created.activated
                  ? t(
                      'AgentBuilderSwitchedOn',
                      'Switched on for {{brand}}{{when}}. You will find it under Agents with the other agents for this brand.',
                      { brand: brandName, when: scheduleWords ? `, ${lowerFirst(scheduleWords)}` : '' }
                    )
                  : t(
                      'AgentBuilderSavedNotOn',
                      'Saved under Agents for {{brand}}, but it could not be switched on yet — switch it on from its card.',
                      { brand: brandName }
                    )}
              </p>
              <div className='agentsHub__done-actions'>
                <MainSquareButton type='button' value={t('Close', 'Close')} buttonType='light' normal onClick={() => onCreated({ ...created, show: false, edited: editing })} />
                <MainSquareButton
                  type='button'
                  value={t('AgentBuilderShowUnderAgents', 'Show me under Agents')}
                  buttonType='success'
                  normal
                  onClick={() => onCreated({ ...created, show: true, edited: editing })}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className='agentsHub__wizard-panel' data-testid='builder-error'>
            <p className='client-agents__validation' role='alert'>
              {error || t('AgentBuilderStoppedResponding', 'The builder stopped responding.')}
            </p>
            <div className='agentsHub__wizard-actions'>
              <button type='button' className='client-agents__intake-skip' onClick={() => setPhase('wizard')}>
                {t('AgentBuilderChangeAnswers', 'Change my answers')}
              </button>
              <div className='agentsHub__wizard-actions-right'>
                <MainSquareButton type='button' value={t('TryAgain', 'Try again')} buttonType='success' normal onClick={retry} />
              </div>
            </div>
          </div>
        )}
      </div>
    </Popup>
  );
};

export default BuilderWizardPopup;
